import React, { useEffect, useState } from "react";
import { useStep } from "../../../context/stepContext";
import { ChairModel, useChair } from "../../../context/ChairContext";
import { Step } from "./Step";
import { useListVariantCategoryFinishing, useListVariantCovers, VariantCost } from "../../../api/hooks/useChairs";
import { plausibleEvent, toLocalNumber } from "../../../utilities/helper";
import { Loader } from "../../Loader";
import { useColorFilter } from "../../../context/ColorContext";
import {
    useGetColorCartInfo,
    useListCatalogFinishingColors,
    useListColorFilters,
    useListFilteredColors,
} from "../../../api/hooks/useColors";
import { useCover } from "../../../context/CoverContext";
import { useFinishing } from "../../../context/FinishingContext";
import { useSearch } from "../../../context/SearchContext";
import { ChairInfoButton } from "../ChairInfoButton";

type Catalog = {
    id: string;
    title: string;
    detailInformation: string;
    shininess: number;
};

enum CoverBreadCrumb {
    CATEGORY,
    FINISHING,
    COLOR,
}

type CoverStepProps = {
    setCoverStep: (breadcrumb: CoverBreadCrumb) => void;
};

type CoverCategoryProps = CoverStepProps & {
    setCatalogs: (catalogs: Array<Catalog>) => void;
};

type CoverFinishingProps = CoverStepProps & {
    catalogs: Array<Catalog>;
};

type CategoryOptionProps = {
    name: string;
    price: number;
    category: string;
    covers: Array<VariantCost>;
    setCoverStep: (breadcrumb: CoverBreadCrumb) => void;
};

type FinishingOptionProps = {
    disabled?: boolean;
    id: string;
    title: string;
    setCoverStep: (breadcrumb: CoverBreadCrumb) => void;
};

type BreadCrumbProps = {
    current: CoverBreadCrumb;
    setCoverStep: (crumb: CoverBreadCrumb) => void;
};

const BreadCrumbs = (props: BreadCrumbProps) => {
    const crumbs = [
        {
            name: "Material",
            crumb: CoverBreadCrumb.CATEGORY,
        },
        {
            name: "Art",
            crumb: CoverBreadCrumb.FINISHING,
        },
        {
            name: "Farbe",
            crumb: CoverBreadCrumb.COLOR,
        },
    ];
    return (
        <div className={"breadcrumbs"}>
            {crumbs.map((crumb, index) => {
                return (
                    <button
                        key={index}
                        className={`button breadcrumb-button icon-button ${
                            crumb.crumb === props.current ? "current" : ""
                        }`}
                        onClick={() => props.setCoverStep(crumb.crumb)}
                        disabled={crumb.crumb === props.current}
                    >
                        {crumb.name}
                    </button>
                );
            })}
        </div>
    );
};

const SearchFilter = () => {
    const { search, setSearch } = useSearch();

    return (
        <div className={"cover-search"}>
            <label>
                <h5>Bezug Suche</h5>
                <input
                    type={"text"}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.currentTarget.value);
                    }}
                />
            </label>
        </div>
    );
};

const ColorFilter = () => {
    const { colorIds, toggleColorId, empty } = useColorFilter();
    const colorFilterQuery = useListColorFilters();

    const colorFilters = colorFilterQuery.isSuccess && colorFilterQuery.data ? colorFilterQuery.data.hueEntries : [];

    return (
        <div className={"color-filter-wrapper"}>
            <h5>Farbton Filter</h5>
            <ul className={"color-filters"}>
                {colorFilters.map((filter) => {
                    return (
                        <li className={`color-filter`} key={filter.id}>
                            <button
                                className={`${colorIds.indexOf(filter.id.toString()) >= 0 ? "active" : null}`}
                                style={{ backgroundColor: filter.color }}
                                title={filter.title}
                                onClick={() => toggleColorId(filter.id.toString())}
                            >
                                {filter.title}
                            </button>
                        </li>
                    );
                })}
                <li className={`no-filter`}>
                    <button
                        title={"Alle Filter entfernen"}
                        className={`remove-filter ${colorIds.length === 0 ? "disabled" : ""}`}
                        onClick={empty}
                    >
                        X
                    </button>
                </li>
            </ul>
        </div>
    );
};

const CategoryOption = (props: CategoryOptionProps) => {
    const id = useChair((state) => state.chair.id);
    const cover = useChair((state) => state.chair.cover);
    const setChair = useChair((state) => state.setChair);
    const { category, setCategory } = useCover();

    const handleClick = (event) => {
        event.stopPropagation();
        setCategory(props.category);

        if (
            cover?.className !== props.name &&
            (props.name === "Holzsitz" || props.name === "Tischgestell" || props.name.startsWith("Beigestellt"))
        ) {
            const chair: ChairModel = {
                id: id,
                cover: { className: props.name, price: props.price },
            };
            chair.cover.catalog = { name: "", id: "", shininess: 0 };
            chair.cover.color = { url: "", name: "", id: "" };
            setChair(chair);
        }

        if (props.name !== "Holzsitz" && props.name !== "Tischgestell" && !props.name.startsWith("Beigestellt")) {
            props.setCoverStep(CoverBreadCrumb.FINISHING);
        }
        plausibleEvent("cover-category-selected", props.name);
    };

    return (
        <li
            className={`category-option ${
                props.category === category && (category !== "supplied" || props.name === cover.className)
                    ? "current"
                    : ""
            } ${props.name === "Holzsitz" || props.name.startsWith("Beigestellt") ? "no-catalog" : ""}`}
            onClick={handleClick}
        >
            <div className={"category-name"}>{props.name}</div>
        </li>
    );
};

const CoverCategory = (props: CoverCategoryProps) => {
    const id = useChair((state) => state.chair.id);
    const variantCoversQuery = useListVariantCovers({ id: id });
    const { setFabricIds, setLeatherIds } = useCover();

    const variantCovers = variantCoversQuery.isSuccess
        ? (variantCoversQuery.data.chairEntries[0].variantCost ?? [])
        : [];

    useEffect(() => {
        if (variantCoversQuery.isSuccess) {
            const fabricIds = Array<string>();
            const leatherIds = Array<string>();

            variantCoversQuery.data.chairEntries.forEach((variant) => {
                variant.variantCost.forEach((vc) => {
                    vc.cover.forEach((cover) => {
                        if (cover.category === "fabric") {
                            fabricIds.push(cover.id);
                        } else if (cover.category === "leather") {
                            leatherIds.push(cover.id);
                        }
                    });
                });
            });

            setFabricIds(fabricIds);
            setLeatherIds(leatherIds);
        }
    }, [variantCoversQuery.isSuccess]);

    const categories: Array<{ name: string; price: number; category: string }> = variantCovers
        .map((cover) => {
            if (cover.cover.length > 0) {
                if (cover.cover[0].category === "fabric") {
                    return { name: "Stoff", price: 0, category: cover.cover[0].category };
                } else if (cover.cover[0].category === "leather") {
                    return { name: "Leder", price: 0, category: cover.cover[0].category };
                } else {
                    return { name: cover.cover[0].title, price: cover.coverCost, category: cover.cover[0].category };
                }
            }
        })
        .filter((value, index, self) => self.map((category) => category.name).indexOf(value.name) === index);

    return (
        <>
            <BreadCrumbs setCoverStep={props.setCoverStep} current={CoverBreadCrumb.CATEGORY} />
            <h2 className={"heading"}>Wählen Sie Ihr Material</h2>
            {variantCoversQuery.isLoading ? (
                <Loader />
            ) : (
                <ul className={"category-options"}>
                    {categories.map((category) => {
                        return (
                            <CategoryOption
                                key={category.name}
                                price={category.price}
                                name={category.name}
                                category={category.category}
                                covers={variantCovers}
                                setCoverStep={props.setCoverStep}
                            />
                        );
                    })}
                </ul>
            )}
        </>
    );
};

const FinishingOption = (props: FinishingOptionProps) => {
    const { colorIds } = useColorFilter();
    const { finishing, setFinishing } = useFinishing();

    const finishingColorsQuery = useListCatalogFinishingColors({ id: [props.id] });

    const finishingColors =
        finishingColorsQuery.isSuccess && finishingColorsQuery.data
            ? finishingColorsQuery.data.colorCatalogEntries.flatMap((entries) => {
                  return entries.colors.flatMap((color) => {
                      return color.hue.flatMap((hue) => {
                          return hue.id;
                      });
                  });
              })
            : [];

    const disabled = (colorIds.length > 0 && !colorIds.some((e) => finishingColors.includes(e))) || props.disabled;
    const current = props.id === finishing;

    const handleClick = (event) => {
        event.stopPropagation();
        if (!disabled) {
            setFinishing(props.id);
            props.setCoverStep(CoverBreadCrumb.COLOR);
        }
    };

    return (
        <li
            className={`catalog-option ${disabled ? "disabled" : null} ${current ? "current" : null}`}
            aria-disabled={disabled}
            onClick={handleClick}
        >
            <div className={"catalog-name"}>{props.title}</div>
        </li>
    );
};

const CoverFinishing = (props: CoverFinishingProps) => {
    const { category } = useCover();
    const { search } = useSearch();
    const id = useChair((state) => state.chair.id);

    const finishingQuery = useListVariantCategoryFinishing({ id: id, category: category, search: search });

    const variantFinishingResult =
        finishingQuery.isSuccess && finishingQuery.data ? finishingQuery.data.chairEntries : [];

    const variantFinishing = [];

    variantFinishingResult.forEach((variant) => {
        variant.variantCost.forEach((cost) => {
            cost.cover.forEach((cover) => {
                cover.collection.forEach((collection) => {
                    collection.finishing.forEach((finishing) => {
                        const index = variantFinishing.map((vf) => vf.title).indexOf(finishing.title);
                        if (index < 0) {
                            variantFinishing.push({ ...finishing, colors: collection.colors.length });
                        } else {
                            variantFinishing[index] = {
                                ...variantFinishing[index],
                                colors: variantFinishing[index].colors + collection.colors.length,
                            };
                        }
                    });
                });
            });
        });
    });

    return (
        <>
            <BreadCrumbs setCoverStep={props.setCoverStep} current={CoverBreadCrumb.FINISHING} />
            <div className={"filter-wrapper"}>
                <ColorFilter />
                <SearchFilter />
            </div>
            <h2 className={"heading"}>Wählen Sie die Material Art</h2>
            {finishingQuery.isLoading ? (
                <Loader />
            ) : variantFinishing.length > 0 ? (
                <div className={"catalog-content"}>
                    {finishingQuery.isFetching ? <Loader className={"loader-overlay"} /> : null}
                    <ul className={"catalog-options"}>
                        {variantFinishing.map((finishing) => {
                            return (
                                <FinishingOption
                                    id={finishing.id}
                                    title={finishing.title}
                                    key={finishing.id}
                                    disabled={finishing.colors === 0}
                                    setCoverStep={props.setCoverStep}
                                />
                            );
                        })}
                    </ul>
                </div>
            ) : (
                "Leider keine Material Art für das gewählte Modell verfügbar"
            )}
        </>
    );
};

const ColorOption = (props: { id: string; title: string; url: string; enabled: boolean }) => {
    const id = useChair((state) => state.chair.id);
    const cover = useChair((state) => state.chair.cover);
    const setChair = useChair((state) => state.setChair);
    const [cartInfo, setCartInfo] = useState<{
        coverName: string;
        shininess: number;
        detailInformation: string;
        collectionId: string;
        collectionName: string;
        colorId: string;
        cost: number;
        repeat: number;
    } | null>(null);

    const cartInfoQuery = useGetColorCartInfo({ id: id, cover: props.id }, props.enabled);

    useEffect(() => {
        if (cartInfoQuery.isSuccess) {
            cartInfoQuery.data.chairEntries.forEach((entry) => {
                entry.variantCost.forEach((cost) => {
                    cost.cover.forEach((cover) => {
                        cover.collection.forEach((collection) => {
                            if (collection.colors.length > 0 && collection.colors[0].id === props.id) {
                                setCartInfo({
                                    coverName: cover.title,
                                    shininess: collection.shininess,
                                    detailInformation: collection.detailInformation,
                                    collectionId: collection.id,
                                    collectionName: collection.title,
                                    colorId: props.id,
                                    cost: cost.coverCost,
                                    repeat: collection.patternRepeat,
                                });
                                return;
                            }
                        });
                    });
                });
            });
        }
    }, [cartInfoQuery.isSuccess]);

    const getTextureUrl = () => {
        const parts = props.url.split("/");
        parts.splice(parts.length - 2, 1);
        return parts.join("/");
    };

    const handleClick = (event) => {
        event.stopPropagation();
        if (cartInfo) {
            setChair({
                id: id,
                cover: {
                    className: cartInfo.coverName,
                    price: cartInfo.cost,
                    catalog: {
                        repeat: cartInfo.repeat,
                        name: cartInfo.collectionName,
                        id: cartInfo.collectionId,
                        shininess: cartInfo.shininess,
                    },
                    color: { url: getTextureUrl(), name: props.title, id: props.id },
                },
            });

            plausibleEvent("color-selected", props.title);
        }
    };

    return (
        <li
            className={`color-option ${!cartInfo ? "loading" : ""} ${cover?.color?.id === props.id ? "current" : ""}`}
            onClick={(event) => {
                if (cartInfo) {
                    handleClick(event);
                }
            }}
        >
            {cartInfo ? (
                <img className={"color-preview"} src={props.url} width={50} height={50} alt={props.title} />
            ) : (
                <Loader className={"color-loader"} />
            )}

            <div className={"color-name"}>{props.title}</div>
            {cartInfo && cartInfo.detailInformation ? (
                <div className={"color-info"}>
                    <div className={"color-details"}>
                        {"Name: " + props.title} <br />
                        {cartInfo.detailInformation.split("\n").map((item, index) => {
                            return (
                                <React.Fragment key={index}>
                                    {item}
                                    <br />
                                </React.Fragment>
                            );
                        })}
                        {"Preis: " + toLocalNumber(cartInfo.cost) + "€"}
                    </div>
                </div>
            ) : (
                <div className={"color-info"}></div>
            )}
            <input type={"radio"} checked={cover?.color?.id === props.id} onChange={handleClick} />
        </li>
    );
};

const CoverColorOptions = (props: {
    isFetching: boolean;
    colors: Array<{ id: string; title: string; url: string }>;
}) => {
    const { colorIds } = useColorFilter();
    const { search } = useSearch();
    const [page, setPage] = useState<number>(0);
    const [enabled, setEnabled] = useState<boolean>(false);
    const entries = 8;

    const currentPage = props.colors.slice(page * entries, page * entries + entries);

    useEffect(() => {
        setPage(0);
    }, [colorIds, search]);

    useEffect(() => {
        setEnabled(false);
        const id = window.setTimeout(() => {
            setEnabled(true);
        }, 400);

        return () => {
            window.clearTimeout(id);
        };
    }, [page]);

    return (
        <div className={`color-options-wrapper ${props.isFetching ? "loading" : ""}`}>
            {props.colors.length > 0 ? (
                <ul className={"color-options"}>
                    {currentPage.map((color) => {
                        return (
                            <ColorOption
                                key={color.id}
                                id={color.id}
                                title={color.title}
                                url={color.url}
                                enabled={enabled}
                            />
                        );
                    })}
                </ul>
            ) : (
                "Keine Einträge gefunden"
            )}

            {props.isFetching ? <Loader className={"color-options-loader"} /> : null}
            <div className={"color-buttons"}>
                <button
                    className={`button only-icon secondary previous-colors `}
                    disabled={!(page > 0)}
                    onClick={(e) => {
                        setPage(page - 1);
                        e.currentTarget.blur();
                    }}
                >
                    Vorige Seite
                </button>
                <div className={"pages"}>{page + 1 + "/" + Math.max(Math.ceil(props.colors.length / entries), 1)}</div>
                <button
                    className={`button only-icon secondary next-colors`}
                    disabled={props.colors.length <= (page + 1) * entries}
                    onClick={(e) => {
                        e.currentTarget.blur();
                        setPage(page + 1);
                    }}
                >
                    Nächste Seite
                </button>
            </div>
        </div>
    );
};
const CoverColor = (props: CoverStepProps) => {
    const { colorIds } = useColorFilter();
    const { category, fabricIds, leatherIds } = useCover();
    const { finishing } = useFinishing();
    const { search } = useSearch();

    const colorsQuery = useListFilteredColors({
        color: colorIds,
        category: category,
        finishing: finishing,
        coverIds: category === "fabric" ? fabricIds : leatherIds,
        search: search,
    });

    const colors =
        colorsQuery.isSuccess && colorsQuery.data
            ? colorsQuery.data.colorCatalogEntries
                  .flatMap((entry) => {
                      if (entry._count > 0) {
                          return entry.colors;
                      }
                  })
                  .filter((value) => !!value)
            : [];

    return (
        <>
            <BreadCrumbs setCoverStep={props.setCoverStep} current={CoverBreadCrumb.COLOR} />
            <div className={"filter-wrapper"}>
                <ColorFilter />
                <SearchFilter />
            </div>
            <h2 className={"heading"}>Wählen Sie einen Bezug</h2>

            {colorsQuery.isInitialLoading ? null : (
                <CoverColorOptions colors={colors} isFetching={colorsQuery.isFetching} />
            )}
        </>
    );
};

export const CoverStep = () => {
    const { setStep } = useStep();
    // const chairCover = useChair((state) => state.chair.cover);
    const [catalogs, setCatalogs] = useState<Array<Catalog>>([]);
    const [coverStep, setCoverStep] = useState<CoverBreadCrumb>(CoverBreadCrumb.CATEGORY);

    const componentToBreadCrumb: Map<CoverBreadCrumb, JSX.Element> = new Map([
        [CoverBreadCrumb.CATEGORY, <CoverCategory setCoverStep={setCoverStep} setCatalogs={setCatalogs} />],
        [CoverBreadCrumb.FINISHING, <CoverFinishing setCoverStep={setCoverStep} catalogs={catalogs} />],
        [CoverBreadCrumb.COLOR, <CoverColor setCoverStep={setCoverStep} />],
    ]);

    return (
        <div className={"step cover-step"}>
            <button
                className={"button back-button icon-button"}
                onClick={() => {
                    setStep(Step.OVERVIEW);
                }}
            >
                Zurück
            </button>
            <ChairInfoButton />
            {componentToBreadCrumb.get(coverStep)}
        </div>
    );
};
